import * as React from 'react';
import { useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';
import DeleteIcon from '@mui/icons-material/Delete';
import { Button, CircularProgress, TextField } from '@mui/material';

import { Customer } from 'types/Customer';
import { updateCustomer, deleteCustomer } from 'services/clients/customers';
import { DeletePrompt } from './DeletePrompt';
import { MAX_CODE_LENGTH, validateCode } from '../../utility';

interface Props {
  customer: Customer;
  onClose: () => void;
}

export const Edit: React.FC<Props> = (props) => {
  const { customer, onClose } = props;
  const [promptForDelete, setPromptForDelete] = React.useState(false);

  const [code, setCode] = React.useState(customer.code);

  const isDeleting = useIsMutating(['deleteCustomer', customer.id]) > 0;

  const queryClient = useQueryClient();
  const updateMutation = useMutation(updateCustomer, {
    mutationKey: ['updateCustomer', customer.id],
    onSuccess: () => {
      queryClient.invalidateQueries(['customers']);
      queryClient.invalidateQueries(['customerTag', customer.id]);
    },
  });

  React.useEffect(() => {
    setCode(customer.code);
  }, [customer]);

  const saveCustomer = React.useCallback(() => {
    const errors = validateCode(code);
    if (code && errors.length === 0) {
      updateMutation.mutate({ ...customer, code });
    }
  }, [updateMutation, code, customer]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newCode = e.target.value;
    setCode(newCode);
  };

  const deleteMutation = useMutation(deleteCustomer, {
    mutationKey: ['deleteCustomer', customer.id],
    onSuccess: () => {
      queryClient.invalidateQueries(['customers']);
      queryClient.invalidateQueries(['watchList']);
    },
  });

  const handleDelete = React.useCallback(() => {
    deleteMutation.mutateAsync(customer.id);
    setPromptForDelete(false);
  }, [deleteMutation, customer, setPromptForDelete]);

  const handleDeleteClick = () => {
    if (customer.count > 0) {
      setPromptForDelete(true);
    } else {
      handleDelete();
    }
  };

  const deleteElement = promptForDelete ? (
    <DeletePrompt
      onDelete={handleDelete}
      onCancel={() => setPromptForDelete(false)}
      customer={customer}
    />
  ) : (
    <Button className="text-gray-800" onClick={handleDeleteClick}>
      <DeleteIcon />
      <h4>Delete</h4>
    </Button>
  );

  return (
    <div className="w-full h-full space-y-4" onKeyDown={(e) => e.stopPropagation()}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          saveCustomer();
          onClose();
        }}
      >
        <TextField
          value={code}
          onChange={handleChange}
          fullWidth
          size="small"
          inputProps={{ maxLength: MAX_CODE_LENGTH }}
          disabled={isDeleting}
        />
      </form>
      {isDeleting ? (
        <h4>
          Deleting… <CircularProgress size={10} />
        </h4>
      ) : (
        deleteElement
      )}
    </div>
  );
};
Edit.displayName = 'CustomerTag.Search.Edit';
